import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../api/client';
import '../../components/kits/KitPopup.css';
import './SetPopup.css';
import MarketplacesField, { ALL_MARKETPLACES } from './MarketplacesField';
import AlternateIdsField from './AlternateIdsField';
import ProductDetailsField from './ProductDetailsField';

const SetPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [set, setSet] = useState({
        SKU: '', setmane: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '', niche_category_id: '',
        bagsPerBox: '', locationWH: '', produce_per_shift: '', isActive: 1,
        marketplaces: ALL_MARKETPLACES,
    });
    const [composition, setComposition] = useState([]); // [{sbSKU, packName, brandID}]
    const [allPackets,  setAllPackets]  = useState([]);  // full list for dropdown
    const [brands,      setBrands]      = useState([]);  // for brand select
    const [niches,      setNiches]      = useState([]);  // for niche select
    const [search,      setSearch]      = useState('');
    const [dropOpen,    setDropOpen]    = useState(false);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState('');

    const searchRef   = useRef(null);
    const inputRef    = useRef(null);
    const [dropStyle, setDropStyle] = useState({});

    // Load packets + brands + niches once on open
    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/options/individualpackets')
            .then(r => setAllPackets(r.data.items || []))
            .catch(() => {});
        api.get('/api/options/brands')
            .then(r => setBrands(r.data.items || []))
            .catch(() => {});
        api.get('/api/options/niche_category')
            .then(r => setNiches(r.data.items || []))
            .catch(() => {});
    }, [isOpen]);

    // Load set + composition when editing
    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setSet({
                SKU:               editData.SKU               || '',
                setmane:           editData.setmane           || '',
                ASIN:              editData.ASIN              || '',
                FNSKU:             editData.FNSKU             || '',
                GTIN:              editData.GTIN              || '',
                brandID:           editData.brandID           || '',
                niche_category_id: editData.niche_category_id || '',
                bagsPerBox:        editData.bagsPerBox        || '',
                locationWH:        editData.locationWH        || '',
                produce_per_shift: editData.produce_per_shift || '',
                isActive:          editData.isActive != null ? editData.isActive : 1,
                marketplaces:      editData.marketplaces != null ? editData.marketplaces : ALL_MARKETPLACES,
            });
            api.get(`/api/sets/${editData.SKU}/composition`)
                .then(r => setComposition(r.data.map(c => ({
                    sbSKU:    c.sbSKU,
                    packName: c.packName,
                    brandID:  c.brandID,
                    quantity: Number(c.quantity) || 1,
                }))))
                .catch(() => setComposition([]));
        } else {
            setSet({ SKU: '', setmane: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '', niche_category_id: '',
                     bagsPerBox: '', locationWH: '', produce_per_shift: '', isActive: 1,
                     marketplaces: ALL_MARKETPLACES });
            setComposition([]);
        }
        setSearch(''); setDropOpen(false); setError('');
    }, [isOpen, editData]);

    // Close dropdown on outside click
    useEffect(() => {
        const h = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target))
                setDropOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    // Dropdown: filter out already-added packets
    // Must be before the early return so hooks are always called in the same order
    const filtered = useMemo(() => {
        if (!isOpen) return [];
        const addedSkus = new Set(composition.map(c => c.sbSKU));
        const q = search.trim().toLowerCase();
        return allPackets.filter(p =>
            !addedSkus.has(p.SKU) &&
            (!q || (p.packName || '').toLowerCase().includes(q) ||
                    (p.SKU || '').toLowerCase().includes(q))
        );
    }, [isOpen, allPackets, search, composition]);

    if (!isOpen) return null;

    const setField = (field) => (e) => setSet(p => ({ ...p, [field]: e.target.value }));

    const addPacket = (packet) => {
        setComposition(prev => [...prev, {
            sbSKU:    packet.SKU,
            packName: packet.packName || packet.SKU,
            brandID:  packet.brandID  || '',
            quantity: 1,
        }]);
        setSearch('');
        setDropOpen(false);
        inputRef.current?.focus();
    };

    const removePacket = (sbSKU) =>
        setComposition(prev => prev.filter(c => c.sbSKU !== sbSKU));

    const setPacketQty = (sbSKU, qty) =>
        setComposition(prev => prev.map(c =>
            c.sbSKU === sbSKU ? { ...c, quantity: Math.max(1, qty) } : c
        ));

    const tagLabel = (c) =>
        c.packName
            ? (c.brandID ? `${c.brandID} — ${c.packName}` : c.packName)
            : c.sbSKU;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!set.SKU.trim()) { setError('SKU is required'); return; }
        setSaving(true);
        try {
            const payload = {
                ...set,
                composition: composition.map(c => ({ sbSKU: c.sbSKU, quantity: c.quantity || 1 })),
            };
            if (editData) {
                await api.put(`/api/set-popup/${editData.SKU}`, payload);
            } else {
                await api.post('/api/set-popup', payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup">

                {/* Header */}
                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData ? `Edit Set — ${editData.setmane || editData.SKU}` : 'Add New Set'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? 'Update set details and composition' : 'Fill in set details and add packets'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <use xlinkHref="#closeBtn" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="kit-popup__body">

                    {/* ── Left: set fields ── */}
                    <div className="kit-popup__info">

                        <div className="kit-field">
                            <span className="kit-field__label">SKU *</span>
                            <input className="kit-field__input" value={set.SKU}
                                readOnly={!!editData} onChange={setField('SKU')}
                                placeholder="e.g. ORSCH_20_09" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Name</span>
                            <input className="kit-field__input" value={set.setmane}
                                onChange={setField('setmane')} placeholder="Set display name" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">ASIN</span>
                            <input className="kit-field__input" value={set.ASIN}
                                onChange={setField('ASIN')} placeholder="Amazon ASIN" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">FNSKU</span>
                            <input className="kit-field__input" value={set.FNSKU}
                                onChange={setField('FNSKU')} placeholder="Amazon FNSKU" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">GTIN</span>
                            <input className="kit-field__input" value={set.GTIN}
                                onChange={setField('GTIN')} placeholder="Barcode / GTIN" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Brand</span>
                            <select className="kit-field__input"
                                value={set.brandID}
                                onChange={e => setSet(p => ({ ...p, brandID: e.target.value }))}>
                                <option value="">— Select brand —</option>
                                {brands.map(b => (
                                    <option key={b.brandID} value={b.brandID}>{b.brandName}</option>
                                ))}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Niche</span>
                            <select className="kit-field__input"
                                value={set.niche_category_id}
                                onChange={e => setSet(p => ({ ...p, niche_category_id: e.target.value }))}>
                                <option value="">— Select niche —</option>
                                {niches.map(n => (
                                    <option key={n.niche_category_id} value={n.niche_category_id}>{n.niche_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Bags per Box</span>
                            <input className="kit-field__input" type="number" min="1"
                                value={set.bagsPerBox} onChange={setField('bagsPerBox')} placeholder="e.g. 80" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Produce per Shift</span>
                            <input className="kit-field__input" type="number" min="1"
                                value={set.produce_per_shift} onChange={setField('produce_per_shift')} placeholder="e.g. 480" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">WH Location</span>
                            <input className="kit-field__input" value={set.locationWH}
                                onChange={setField('locationWH')} placeholder="Warehouse location" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Active</span>
                            <select className="kit-field__input"
                                value={set.isActive}
                                onChange={e => setSet(p => ({ ...p, isActive: parseInt(e.target.value) }))}>
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                            </select>
                        </div>

                        <MarketplacesField
                            value={set.marketplaces}
                            onChange={v => setSet(p => ({ ...p, marketplaces: v }))}
                        />

                    </div>

                    {/* ── Right: composition ── */}
                    <div className="kit-popup__components">

                        <div className="kit-comp__toolbar">
                            <span className="kit-comp__heading">
                                Composition
                                {composition.length > 0 && (
                                    <span className="kit-comp__count">
                                        {composition.reduce((s, c) => s + (c.quantity || 1), 0)}
                                    </span>
                                )}
                            </span>
                        </div>

                        <div className="set-comp__body">

                            {/* Search + dropdown */}
                            <div className="set-comp__search-wrap" ref={searchRef}>
                                <input
                                    ref={inputRef}
                                    className="set-comp__search-input"
                                    placeholder="Search packets by name or SKU to add…"
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setDropOpen(true); }}
                                    onFocus={() => {
                                        if (inputRef.current) {
                                            const r = inputRef.current.getBoundingClientRect();
                                            setDropStyle({ top: r.bottom + 4, left: r.left, width: r.width });
                                        }
                                        setDropOpen(true);
                                    }}
                                />
                                {dropOpen && (search.trim() !== '' || filtered.length > 0) && (
                                    <div className="set-comp__dropdown" style={dropStyle}>
                                        {filtered.length === 0 ? (
                                            <div className="set-comp__dropdown-empty">
                                                {search.trim() ? 'No packets found' : 'Start typing to search…'}
                                            </div>
                                        ) : (
                                            filtered.map(p => (
                                                <div
                                                    key={p.SKU}
                                                    className="set-comp__dropdown-item"
                                                    onMouseDown={() => addPacket(p)}
                                                >
                                                    <span className="set-comp__item-name">
                                                        {p.brandID
                                                            ? <><span className="set-comp__item-brand">{p.brandID}</span> — {p.packName}</>
                                                            : p.packName}
                                                    </span>
                                                    <span className="set-comp__item-sku">{p.SKU}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Tags */}
                            {composition.length > 0 ? (
                                <div className="set-comp__tags">
                                    {composition.map(c => (
                                        <span key={c.sbSKU} className="set-comp__tag">
                                            {tagLabel(c)}
                                            <span className="set-comp__tag-qty">
                                                <button
                                                    type="button"
                                                    className="set-comp__tag-qty-btn"
                                                    onClick={() => setPacketQty(c.sbSKU, (c.quantity || 1) - 1)}
                                                    disabled={(c.quantity || 1) <= 1}
                                                    title="Decrease quantity"
                                                >−</button>
                                                <span className="set-comp__tag-qty-value" title="Packets of this SKU in the set">
                                                    ×{c.quantity || 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="set-comp__tag-qty-btn"
                                                    onClick={() => setPacketQty(c.sbSKU, (c.quantity || 1) + 1)}
                                                    title="Increase quantity"
                                                >+</button>
                                            </span>
                                            <button
                                                type="button"
                                                className="set-comp__tag-remove"
                                                onClick={() => removePacket(c.sbSKU)}
                                                title="Remove"
                                            >×</button>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <div className="set-comp__empty">
                                    <span style={{ fontSize: 28, opacity: .3 }}>🗂</span>
                                    <span style={{ fontSize: 13, color: '#94a3b8' }}>
                                        No packets yet — search above to add
                                    </span>
                                </div>
                            )}

                            {editData && (
                                <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #eef1f4' }}>
                                    <ProductDetailsField productType="sets" sku={editData.SKU} />
                                    <AlternateIdsField productType="sets" sku={editData.SKU} />
                                </div>
                            )}

                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Set')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default SetPopup;
